import React, { useState, useEffect } from 'react';
import NotificationSettingsWrapper from '../../../components/NotificationSettings/NotificationSettingsWrapper';
import { useGetProducts, useGetNotification, usePostNotification, usePutNotification, useDeleteNotification } from '../hooks/api/notificationApi';
import useAppContext from '../../../hooks/context/useAppContext';
import { useGetContract } from '../../ContractDetailPage/hooks/api/contractDetailApi';
import { formattedNumber } from '../../../utils/number';
import { useIntl } from 'react-intl';

import PropTypes from 'prop-types';

const notificationSkelet = (sms, email, enums) => {
  const skeletArray = [];
  const { notificationChannels } = enums();
  if (sms) {
    const smsId = notificationChannels.filter((item) => item.name === 'sms')[0]?.id;
    skeletArray.push({
      id: smsId,
    });
  }
  if (email) {
    const emailId = notificationChannels.filter((item) => item.name === 'email')[0]?.id;
    skeletArray.push({
      id: emailId,
    });
  }
  return skeletArray;
};

const setNotificationChannel = (notificationChannel, enums, type) => {
  const { notificationChannels } = enums();
  const typeId = notificationChannels.find((item) => item.name === type)?.id;
  if (notificationChannel.find((item) => item.id === typeId)) {
    return true;
  }
  return false;
};

const NotificationSettings = ({ id }) => {
  const { getProducts, data: productsData, loading: productLoading } = useGetProducts();
  const { getContract, data: contractData } = useGetContract();
  const { getNotification, data: notificationData } = useGetNotification();
  const { postNotification, data: postNotificationData, error: postError, loading: postLoading } = usePostNotification();
  const { putNotification, error: putError, loading: putLoading } = usePutNotification();
  const { deleteNotification, data: deleteData, error: deleteError, loading: deleteLoading } = useDeleteNotification();

  const [sms, setSms] = useState(false);
  const [email, setEmail] = useState(false);
  const [productId, setProductId] = useState('');
  const [loggedUser, setLoggedUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [name, setName] = useState('');
  const [contractId, setContractId] = useState('');
  const [currencyId, setCurrencyId] = useState('');
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(0);
  const [price, setPrice] = useState(0);
  const intl = useIntl();
  const { setBreadCrumbs } = useAppContext();

  const pricesAlertTitle = intl.formatMessage({ id: 'app.pricesAlert.title' });

  useEffect(() => {
    setBreadCrumbs(`${pricesAlertTitle}`);
  }, [setBreadCrumbs, pricesAlertTitle]);

  const sendHandler = () => {
    const body = {
      user: {
        id: loggedUser?.id,
      },
      contract: {
        id: Number(contractId),
      },
      exchangeProduct: {
        id: Number(productId),
      },
      currency: {
        id: currencyId,
      },
      minPrice: formattedNumber(minPrice, 3),
      maxPrice: formattedNumber(maxPrice, 3),
      name: name,
      notificationChannels: notificationSkelet(sms, email, getEnums),
    };
    if (id !== 'new') {
      Object.assign(body, { id: Number(id) });
      putNotification(id, body);
    } else {
      postNotification(body);
    }
  };

  const deleteNotifications = () => {
    deleteNotification(id);
  };

  useEffect(() => {
    if (contractData) {
      setCurrencyId(contractData?.currency?.id);
    }
  }, [contractData]);

  useEffect(() => {
    if (id !== 'new') {
      (async () => {
        await getNotification(id);
      })();
    }
  }, [id, getNotification]);

  useEffect(() => {
    if (productId !== '' && contractId) {
      (async () => {
        await getContract(contractId);
      })();
    }
  }, [productId, getContract, contractId]);

  const { getUser, getEnums } = useAppContext();

  useEffect(() => {
    setLoggedUser(getUser());
  }, [getUser]);

  useEffect(() => {
    if (products && productId) {
      const value = products.find((item) => item.id === Number(productId))?.price;
      value ? setPrice(value) : setPrice(0);
    }
  }, [products, productId]);

  useEffect(() => {
    if (products) {
      setContractId(products.filter((item) => item.id === Number(productId))[0]?.idContract);
    }
  }, [setContractId, products, productId]);

  useEffect(() => {
    setLoggedUser(getUser());
    loggedUser && getProducts(loggedUser?.id);
  }, [getUser, getProducts, loggedUser]);

  useEffect(() => {
    if (productsData) {
      setProducts(productsData);
    }
  }, [productsData]);

  useEffect(() => {
    if (notificationData) {
      setProductId(notificationData?.exchangeProduct?.id);
      setMinPrice(notificationData?.minPrice);
      setMaxPrice(notificationData?.maxPrice);
      setName(notificationData?.name);
      setSms(setNotificationChannel(notificationData?.notificationChannels, getEnums, 'sms'));
      setEmail(setNotificationChannel(notificationData?.notificationChannels, getEnums, 'email'));
    }
  }, [notificationData, getEnums]);

  const notification = {
    products,
    productId,
    name,
    minPrice,
    maxPrice,
    sms,
    email,
    currencyId,
    price,
    postError,
    postLoading,
    putError,
    putLoading,
    deleteError,
    deleteLoading,
    productLoading,
    postNotificationData,
    deleteData,
  };

  const handlers = {
    setProductId,
    setName,
    setMinPrice,
    setMaxPrice,
    setSms,
    setEmail,
    sendHandler,
    deleteNotifications,
  };

  return (
    <>
      <NotificationSettingsWrapper id={id} loading={null} error={null} notification={notification} handlers={handlers} />
    </>
  );
};

NotificationSettings.propTypes = {
  id: PropTypes.string,
};
export default NotificationSettings;
